import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid2 as Grid,
  Chip,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  InputAdornment,
  Tooltip,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  LocalOffer as PromoIcon,
  ConfirmationNumber as CouponIcon,
  Settings as SettingsIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import promotionsService, {
  Promotion,
  Coupon,
  PromotionType,
  CouponType,
  CreatePromotionData,
  CreateCouponData,
} from '../../services/promotions.service';
import shopService, { Store } from '../../services/shop.service';
import { useTranslation } from 'react-i18next';
import { dayCv, todayCv } from '../../utils/date';

const Promotions: React.FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [settingUp, setSettingUp] = useState(false);
  const [stores, setStores] = useState<Store[]>([]);

  // Pagination
  const [promoPage, setPromoPage] = useState(0);
  const [promoRowsPerPage, setPromoRowsPerPage] = useState(10);
  const [promoTotal, setPromoTotal] = useState(0);
  const [couponPage, setCouponPage] = useState(0);
  const [couponRowsPerPage, setCouponRowsPerPage] = useState(10);
  const [couponTotal, setCouponTotal] = useState(0);

  // Filters
  const [search, setSearch] = useState('');
  const [storeFilter, setStoreFilter] = useState<number | ''>('');
  const [activeFilter, setActiveFilter] = useState<'' | 'true' | 'false'>('');

  // Dialogs
  const [promoDialog, setPromoDialog] = useState(false);
  const [couponDialog, setCouponDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{ type: 'promo' | 'coupon'; id: number } | null>(null);
  const [editingPromo, setEditingPromo] = useState<Promotion | null>(null);
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null);

  // Form data
  const [promoForm, setPromoForm] = useState<CreatePromotionData>({
    storeId: 0,
    name: '',
    description: '',
    type: 'percentual',
    value: 0,
    minValue: 0,
    maxDiscount: null,
    startDate: todayCv(),
    endDate: dayCv(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)),
    active: true,
    maxUsage: null,
  });

  const [couponForm, setCouponForm] = useState<CreateCouponData>({
    storeId: null,
    code: '',
    name: '',
    description: '',
    type: 'percentual',
    value: 0,
    minValue: 0,
    maxDiscount: null,
    startDate: todayCv(),
    endDate: dayCv(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)),
    active: true,
    maxUsage: null,
    usagePerCustomer: 1,
    firstPurchaseOnly: false,
  });

  const fetchPromotions = useCallback(async () => {
    try {
      const response = await promotionsService.getPromotions({
        page: promoPage + 1,
        limit: promoRowsPerPage,
        storeId: storeFilter || undefined,
        active: activeFilter ? activeFilter === 'true' : undefined,
        search: search || undefined,
      });

      if (response.success) {
        setPromotions(response.data || []);
        setPromoTotal(response.pagination?.total || 0);
      }
    } catch (err: any) {
      console.error('Error fetching promotions:', err);
      if (err.response?.data?.error?.includes('doesn\'t exist')) {
        setNeedsSetup(true);
      }
    }
  }, [promoPage, promoRowsPerPage, storeFilter, activeFilter, search]);

  const fetchCoupons = useCallback(async () => {
    try {
      const response = await promotionsService.getCoupons({
        page: couponPage + 1,
        limit: couponRowsPerPage,
        storeId: storeFilter || undefined,
        active: activeFilter ? activeFilter === 'true' : undefined,
        search: search || undefined,
      });

      if (response.success) {
        setCoupons(response.data || []);
        setCouponTotal(response.pagination?.total || 0);
      }
    } catch (err: any) {
      console.error('Error fetching coupons:', err);
      if (err.response?.data?.error?.includes('doesn\'t exist')) {
        setNeedsSetup(true);
      }
    }
  }, [couponPage, couponRowsPerPage, storeFilter, activeFilter, search]);

  const fetchStores = useCallback(async () => {
    try {
      const response = await shopService.getStores({ limit: 100 });
      if (response.success) {
        setStores(response.data || []);
      }
    } catch (err) {
      console.error('Error fetching stores:', err);
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([
        activeTab === 0 ? fetchPromotions() : fetchCoupons(),
        fetchStores(),
      ]);
    } catch (err) {
      setError(t('shop.errorLoadingData'));
    } finally {
      setLoading(false);
    }
  }, [activeTab, fetchPromotions, fetchCoupons, fetchStores]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSetup = async () => {
    setSettingUp(true);
    try {
      await promotionsService.setupTables();
      setNeedsSetup(false);
      fetchData();
    } catch (err: any) {
      setError(t('shop.errorSetupPromotions', { message: err.message || t('common.error') }));
    } finally {
      setSettingUp(false);
    }
  };

  const handleSavePromotion = async () => {
    try {
      if (editingPromo) {
        await promotionsService.updatePromotion(editingPromo.id, promoForm);
        setSuccess(t('shop.promoUpdated'));
      } else {
        await promotionsService.createPromotion(promoForm);
        setSuccess(t('shop.promoCreated'));
      }
      setPromoDialog(false);
      resetPromoForm();
      fetchPromotions();
    } catch (err: any) {
      setError(err.response?.data?.error || t('shop.promoSaveError'));
    }
  };

  const handleSaveCoupon = async () => {
    try {
      if (editingCoupon) {
        await promotionsService.updateCoupon(editingCoupon.id, couponForm);
        setSuccess(t('shop.couponUpdated'));
      } else {
        await promotionsService.createCoupon(couponForm);
        setSuccess(t('shop.couponCreated'));
      }
      setCouponDialog(false);
      resetCouponForm();
      fetchCoupons();
    } catch (err: any) {
      setError(err.response?.data?.error || t('shop.couponSaveError'));
    }
  };

  const handleDeletePromotion = async (id: number) => {
    try {
      await promotionsService.deletePromotion(id);
      setSuccess(t('shop.promoDeleted'));
      setDeleteDialog(null);
      fetchPromotions();
    } catch (err: any) {
      setError(err.response?.data?.error || t('shop.promoDeleteError'));
    }
  };

  const handleDeleteCoupon = async (id: number) => {
    try {
      await promotionsService.deleteCoupon(id);
      setSuccess(t('shop.couponDeleted'));
      setDeleteDialog(null);
      fetchCoupons();
    } catch (err: any) {
      setError(err.response?.data?.error || t('shop.couponDeleteError'));
    }
  };

  const resetPromoForm = () => {
    setEditingPromo(null);
    setPromoForm({
      storeId: 0,
      name: '',
      description: '',
      type: 'percentual',
      value: 0,
      minValue: 0,
      maxDiscount: null,
      startDate: todayCv(),
      endDate: dayCv(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)),
      active: true,
      maxUsage: null,
    });
  };

  const resetCouponForm = () => {
    setEditingCoupon(null);
    setCouponForm({
      storeId: null,
      code: '',
      name: '',
      description: '',
      type: 'percentual',
      value: 0,
      minValue: 0,
      maxDiscount: null,
      startDate: todayCv(),
      endDate: dayCv(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)),
      active: true,
      maxUsage: null,
      usagePerCustomer: 1,
      firstPurchaseOnly: false,
    });
  };

  const openEditPromo = (promo: Promotion) => {
    setEditingPromo(promo);
    setPromoForm({
      storeId: promo.storeId,
      name: promo.name,
      description: promo.description || '',
      type: promo.type,
      value: promo.value,
      minValue: promo.minValue,
      maxDiscount: promo.maxDiscount,
      startDate: promo.startDate.split('T')[0],
      endDate: promo.endDate.split('T')[0],
      active: promo.active,
      maxUsage: promo.maxUsage,
    });
    setPromoDialog(true);
  };

  const openEditCoupon = (coupon: Coupon) => {
    setEditingCoupon(coupon);
    setCouponForm({
      storeId: coupon.storeId,
      code: coupon.code,
      name: coupon.name,
      description: coupon.description || '',
      type: coupon.type,
      value: coupon.value,
      minValue: coupon.minValue,
      maxDiscount: coupon.maxDiscount,
      startDate: coupon.startDate.split('T')[0],
      endDate: coupon.endDate.split('T')[0],
      active: coupon.active,
      maxUsage: coupon.maxUsage,
      usagePerCustomer: coupon.usagePerCustomer,
      firstPurchaseOnly: coupon.firstPurchaseOnly,
    });
    setCouponDialog(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-CV', { timeZone: 'UTC',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSuccess(t('shop.codeCopied'));
  };

  if (needsSetup) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="info"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleSetup}
              disabled={settingUp}
              startIcon={settingUp ? <CircularProgress size={16} /> : <SettingsIcon />}
            >
              {settingUp ? t('shop.configuring') : t('shop.setupNow')}
            </Button>
          }
        >
          {t('shop.setupPromotionsMsg')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={600}>
          {t('shop.promotionsTitle')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
          >
            {t('common.refresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              if (activeTab === 0) {
                resetPromoForm();
                setPromoDialog(true);
              } else {
                resetCouponForm();
                setCouponForm(prev => ({ ...prev, code: promotionsService.generateCouponCode() }));
                setCouponDialog(true);
              }
            }}
          >
            {activeTab === 0 ? t('shop.newPromotion') : t('shop.newCoupon')}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <PromoIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" fontWeight={600}>
                {promoTotal}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('shop.promotionsStat')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <CouponIcon sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
              <Typography variant="h4" fontWeight={600}>
                {couponTotal}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('shop.couponsStat')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <PromoIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h4" fontWeight={600}>
                {promotions.filter(p => promotionsService.isPromotionActive(p)).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('shop.activePromotions')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <CouponIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h4" fontWeight={600}>
                {coupons.filter(c => promotionsService.isPromotionActive(c)).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('shop.activeCoupons')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label={t('shop.promotionsTab')} icon={<PromoIcon />} iconPosition="start" />
          <Tab label={t('shop.couponsTab')} icon={<CouponIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('shop.searchReviews')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              select
              label={t('shop.storeLabel')}
              value={storeFilter}
              onChange={(e) => setStoreFilter(e.target.value ? Number(e.target.value) : '')}
            >
              <MenuItem value="">{t('shop.allStoresOption')}</MenuItem>
              {stores.map((store) => (
                <MenuItem key={store.id} value={store.id}>
                  {store.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              select
              label={t('common.status')}
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value as '' | 'true' | 'false')}
            >
              <MenuItem value="">{t('shop.allFilter')}</MenuItem>
              <MenuItem value="true">{t('shop.activeFilterOption')}</MenuItem>
              <MenuItem value="false">{t('shop.inactiveFilterOption')}</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <Button
              fullWidth
              variant="text"
              onClick={() => {
                setSearch('');
                setStoreFilter('');
                setActiveFilter('');
              }}
            >
              {t('shop.clear')}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tables */}
      {activeTab === 0 ? (
        // Promotions Table
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('common.name')}</TableCell>
                <TableCell>{t('shop.storeLabel')}</TableCell>
                <TableCell>{t('common.type')}</TableCell>
                <TableCell>{t('shop.discountCol')}</TableCell>
                <TableCell>{t('shop.periodCol')}</TableCell>
                <TableCell>{t('shop.usageCol')}</TableCell>
                <TableCell>{t('common.status')}</TableCell>
                <TableCell align="center">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : promotions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('shop.noPromotionsFound')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                promotions.map((promo) => (
                  <TableRow key={promo.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {promo.name}
                      </Typography>
                      {promo.description && (
                        <Typography variant="caption" color="text.secondary">
                          {promo.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{promo.storeName || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={promotionsService.getTypeLabel(promo.type)}
                        color={promotionsService.getTypeColor(promo.type)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {promotionsService.formatDiscount(promo.type, promo.value)}
                      </Typography>
                      {promo.minValue > 0 && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {t('shop.minLabel')}: {promotionsService.formatCurrency(promo.minValue)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(promo.startDate)} - {formatDate(promo.endDate)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {promo.maxUsage ? `${promo.currentUsage}/${promo.maxUsage}` : promo.currentUsage}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={promotionsService.getStatusLabel(promo)}
                        color={promotionsService.getStatusColor(promo)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={t('shop.editTooltip')}>
                        <IconButton size="small" onClick={() => openEditPromo(promo)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('shop.deleteConfirmTooltip')}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteDialog({ type: 'promo', id: promo.id })}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={promoTotal}
            page={promoPage}
            onPageChange={(_, p) => setPromoPage(p)}
            rowsPerPage={promoRowsPerPage}
            onRowsPerPageChange={(e) => {
              setPromoRowsPerPage(parseInt(e.target.value, 10));
              setPromoPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage={t('shop.rowsPerPage')}
          />
        </TableContainer>
      ) : (
        // Coupons Table
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('shop.codeCol')}</TableCell>
                <TableCell>{t('common.name')}</TableCell>
                <TableCell>{t('shop.storeLabel')}</TableCell>
                <TableCell>{t('common.type')}</TableCell>
                <TableCell>{t('shop.discountCol')}</TableCell>
                <TableCell>{t('shop.periodCol')}</TableCell>
                <TableCell>{t('shop.usageCol')}</TableCell>
                <TableCell>{t('common.status')}</TableCell>
                <TableCell align="center">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : coupons.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('shop.noCouponsFound')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                coupons.map((coupon) => (
                  <TableRow key={coupon.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight={600} fontFamily="monospace">
                          {coupon.code}
                        </Typography>
                        <Tooltip title={t('shop.copyTooltip')}>
                          <IconButton size="small" onClick={() => copyToClipboard(coupon.code)}>
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{coupon.name}</Typography>
                    </TableCell>
                    <TableCell>{coupon.storeName || t('shop.allRatings')}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={promotionsService.getTypeLabel(coupon.type)}
                        color={promotionsService.getTypeColor(coupon.type)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {promotionsService.formatDiscount(coupon.type, coupon.value)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(coupon.startDate)} - {formatDate(coupon.endDate)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {coupon.maxUsage ? `${coupon.currentUsage}/${coupon.maxUsage}` : coupon.currentUsage}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={promotionsService.getStatusLabel(coupon)}
                        color={promotionsService.getStatusColor(coupon)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={t('shop.editTooltip')}>
                        <IconButton size="small" onClick={() => openEditCoupon(coupon)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('shop.deleteConfirmTooltip')}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteDialog({ type: 'coupon', id: coupon.id })}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={couponTotal}
            page={couponPage}
            onPageChange={(_, p) => setCouponPage(p)}
            rowsPerPage={couponRowsPerPage}
            onRowsPerPageChange={(e) => {
              setCouponRowsPerPage(parseInt(e.target.value, 10));
              setCouponPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage={t('shop.rowsPerPage')}
          />
        </TableContainer>
      )}

      {/* Promotion Dialog */}
      <Dialog open={promoDialog} onClose={() => setPromoDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingPromo ? t('shop.editPromotion') : t('shop.newPromotion')}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('shop.promoNameLabel')}
                value={promoForm.name}
                onChange={(e) => setPromoForm({ ...promoForm, name: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label={t('shop.promoStoreLabel')}
                value={promoForm.storeId || ''}
                onChange={(e) => setPromoForm({ ...promoForm, storeId: Number(e.target.value) })}
                required
              >
                {stores.map((store) => (
                  <MenuItem key={store.id} value={store.id}>
                    {store.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={t('shop.promoDescriptionLabel')}
                value={promoForm.description}
                onChange={(e) => setPromoForm({ ...promoForm, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label={t('shop.promoTypeLabel')}
                value={promoForm.type}
                onChange={(e) => setPromoForm({ ...promoForm, type: e.target.value as PromotionType })}
              >
                <MenuItem value="percentual">{t('shop.percentualType')}</MenuItem>
                <MenuItem value="valor_fixo">{t('shop.fixedValueType')}</MenuItem>
                <MenuItem value="frete_gratis">{t('shop.freeShippingType')}</MenuItem>
                <MenuItem value="compre_ganhe">{t('shop.buyGetType')}</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="number"
                label={promoForm.type === 'percentual' ? t('shop.valuePercent') : t('shop.valueCVE')}
                value={promoForm.value}
                onChange={(e) => setPromoForm({ ...promoForm, value: Number(e.target.value) })}
                disabled={promoForm.type === 'frete_gratis'}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="number"
                label={t('shop.minValueLabel')}
                value={promoForm.minValue}
                onChange={(e) => setPromoForm({ ...promoForm, minValue: Number(e.target.value) })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="number"
                label={t('shop.maxDiscountLabel')}
                value={promoForm.maxDiscount || ''}
                onChange={(e) => setPromoForm({ ...promoForm, maxDiscount: e.target.value ? Number(e.target.value) : null })}
                helperText={t('shop.noLimitHelp')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="date"
                label={t('shop.startDateLabel')}
                value={promoForm.startDate}
                onChange={(e) => setPromoForm({ ...promoForm, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="date"
                label={t('shop.endDateLabel')}
                value={promoForm.endDate}
                onChange={(e) => setPromoForm({ ...promoForm, endDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label={t('shop.maxUsageLabel')}
                value={promoForm.maxUsage || ''}
                onChange={(e) => setPromoForm({ ...promoForm, maxUsage: e.target.value ? Number(e.target.value) : null })}
                helperText={t('shop.noLimitHelp')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={promoForm.active}
                    onChange={(e) => setPromoForm({ ...promoForm, active: e.target.checked })}
                  />
                }
                label={t('shop.activeSwitch')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPromoDialog(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleSavePromotion}>
            {editingPromo ? t('common.refresh') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Coupon Dialog */}
      <Dialog open={couponDialog} onClose={() => setCouponDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingCoupon ? t('shop.editCoupon') : t('shop.newCoupon')}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('shop.couponCodeLabel')}
                value={couponForm.code}
                onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={t('shop.generateCode')}>
                        <IconButton
                          onClick={() => setCouponForm({ ...couponForm, code: promotionsService.generateCouponCode() })}
                        >
                          <RefreshIcon />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('shop.promoNameLabel')}
                value={couponForm.name}
                onChange={(e) => setCouponForm({ ...couponForm, name: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label={t('shop.promoStoreLabel')}
                value={couponForm.storeId || ''}
                onChange={(e) => setCouponForm({ ...couponForm, storeId: e.target.value ? Number(e.target.value) : null })}
              >
                <MenuItem value="">{t('shop.allStoresOption')}</MenuItem>
                {stores.map((store) => (
                  <MenuItem key={store.id} value={store.id}>
                    {store.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label={t('shop.promoTypeLabel')}
                value={couponForm.type}
                onChange={(e) => setCouponForm({ ...couponForm, type: e.target.value as CouponType })}
              >
                <MenuItem value="percentual">{t('shop.percentualType')}</MenuItem>
                <MenuItem value="valor_fixo">{t('shop.fixedValueType')}</MenuItem>
                <MenuItem value="frete_gratis">{t('shop.freeShippingType')}</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={t('shop.promoDescriptionLabel')}
                value={couponForm.description}
                onChange={(e) => setCouponForm({ ...couponForm, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="number"
                label={couponForm.type === 'percentual' ? t('shop.valuePercent') : t('shop.valueCVE')}
                value={couponForm.value}
                onChange={(e) => setCouponForm({ ...couponForm, value: Number(e.target.value) })}
                disabled={couponForm.type === 'frete_gratis'}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="number"
                label={t('shop.minValueLabel')}
                value={couponForm.minValue}
                onChange={(e) => setCouponForm({ ...couponForm, minValue: Number(e.target.value) })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="number"
                label={t('shop.maxDiscountLabel')}
                value={couponForm.maxDiscount || ''}
                onChange={(e) => setCouponForm({ ...couponForm, maxDiscount: e.target.value ? Number(e.target.value) : null })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                type="date"
                label={t('shop.startDateLabel')}
                value={couponForm.startDate}
                onChange={(e) => setCouponForm({ ...couponForm, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                type="date"
                label={t('shop.endDateLabel')}
                value={couponForm.endDate}
                onChange={(e) => setCouponForm({ ...couponForm, endDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                type="number"
                label={t('shop.maxUsageLabel')}
                value={couponForm.maxUsage || ''}
                onChange={(e) => setCouponForm({ ...couponForm, maxUsage: e.target.value ? Number(e.target.value) : null })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                type="number"
                label={t('shop.usagePerCustomer')}
                value={couponForm.usagePerCustomer}
                onChange={(e) => setCouponForm({ ...couponForm, usagePerCustomer: Number(e.target.value) })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={couponForm.active}
                    onChange={(e) => setCouponForm({ ...couponForm, active: e.target.checked })}
                  />
                }
                label={t('shop.activeSwitch')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={couponForm.firstPurchaseOnly}
                    onChange={(e) => setCouponForm({ ...couponForm, firstPurchaseOnly: e.target.checked })}
                  />
                }
                label={t('shop.firstPurchaseOnly')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCouponDialog(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleSaveCoupon}>
            {editingCoupon ? t('common.refresh') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)}>
        <DialogTitle>{t('shop.confirmDeleteTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {deleteDialog?.type === 'promo' ? t('shop.confirmDeletePromo') : t('shop.confirmDeleteCoupon')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)}>{t('common.cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              if (deleteDialog?.type === 'promo') {
                handleDeletePromotion(deleteDialog.id);
              } else if (deleteDialog?.type === 'coupon') {
                handleDeleteCoupon(deleteDialog.id);
              }
            }}
          >
            {t('shop.excludeButton')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Promotions;
